import { useCallback, useEffect, useRef, useState } from 'react'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { PrimaryButton, ErrorAlert } from 'ui_kit_qmd'
import errorIcon from 'images/common/error-icon.svg'
import { getProducts } from 'store/products/productsThunk'
import { type Address } from 'types/types'
import { errorCodes, getUserFriendlyErrorMessage } from 'utils/errormessages'
import { HeaderView } from '../header-view/HeaderView'
import { AddressForm, type AddressFormRef, type AddressError } from './components/address-form/AddressForm'
import { buttonStyle } from '../../utils'
import { useProductOrderContext } from '../../context'
import './completeProductOrderView.scss'
import { ProductSelectedTile } from './components/product-selected-tile/ProductSelectedTile'
import { useSelector } from 'store/hooks/useSelector'
import { useDispatch } from 'react-redux'
import { selectAsyncRefillsPhase1Flag } from 'store/flags/FlagsSelectors'

interface Props {
  address: Address
  onChange: () => void
  onAddOrder: (address: Address, pharmacyId: string | undefined) => void
  tabId: string
}

export const CompleteProductOrderView = ({ address, onChange, onAddOrder, tabId }: Props) => {
  const dispatch = useDispatch()
  const {
    category,
    patientId,
    pharmacy,
    noteError,
    isMoudPharmacyPickup,
    isMoudHomeDelivery,
    isMHTHomeDelivery,
    isMHTPharmacyPickup,
    isMHT,
    isPharmacyHub,
    cartItems,
  } = useProductOrderContext()
  const [error, setError] = useState<string>('')
  const isPharmacyPickup = isMoudPharmacyPickup || isMHTPharmacyPickup
  const asyncRefillsPhase1 = useSelector(selectAsyncRefillsPhase1Flag)
  const showProductTile = !isMHT || (isMHTHomeDelivery && asyncRefillsPhase1)
  const orderAddress = useRef<Address>(address)
  const addressFormRef = useRef<AddressFormRef>()

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('')
      }, 3500)
      return () => clearTimeout(timer)
    }
  }, [error])

  const setAddress = useCallback(
    (submittedAddress: Address) => {
      orderAddress.current = submittedAddress

      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(getProducts(patientId, category, tabId, submittedAddress.StateCode))
    },
    [dispatch, patientId, category, tabId],
  )

  const addOrder = useCallback(() => {
    const addressError: AddressError = {}
    if (pharmacy && !isMoudHomeDelivery && !isMHTHomeDelivery) orderAddress.current = pharmacy.Address

    // Only validate address fields for PharmacyHub (where provider enters address)
    if (isPharmacyHub) {
      if (!orderAddress.current.City) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        addressError.city = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'City')
      }
      if (!orderAddress.current.StreetAddress1) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        addressError.streetAddress1 = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Street address 1')
      }
      if (!orderAddress.current.Zip) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        addressError.zip = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Zip')
      }
    }

    if (Object.keys(addressError).length && addressFormRef?.current) {
      addressFormRef.current.setError(addressError)
      setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
    } else {
      onAddOrder(orderAddress.current, pharmacy?.PharmacyId)
    }
  }, [pharmacy, isMoudHomeDelivery, isMHTHomeDelivery, isPharmacyHub, onAddOrder])

  const handlePrimaryClick = useCallback(() => {
    if (isMoudPharmacyPickup) {
      if (!pharmacy) {
        setError('Please select a pharmacy')
      } else {
        onAddOrder(pharmacy.Address, pharmacy.PharmacyId)
      }
    } else {
      addOrder()
    }
  }, [addOrder, onAddOrder, pharmacy, isMoudPharmacyPickup])

  const buttonText = 'Review order'

  return (
    <div className='product-selection-view-container'>
      <HeaderView title='Confirm delivery details' />

      {error ? <div className='complete-product-order-view-error-container'>
          <ErrorAlert errorText={error} errorImg={errorIcon} imgAlt='Error' />
        </div> : null}

      <div className='product-selection-view-body-container'>
        {showProductTile ? <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {cartItems.map((cartItem) => (
              <ProductSelectedTile key={cartItem.CartId} item={cartItem} onChange={onChange} />
            ))}
          </div> : null}

        {/*
         // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
        <AddressForm ref={addressFormRef} savedAddress={address} setAddress={setAddress} noteError={noteError} />
      </div>

      <div className='product-selection-view-button-container'>
        <PrimaryButton
          disabled={isPharmacyPickup ? !pharmacy : null}
          useUpdatedStyle
          containerStyle={buttonStyle}
          btnText={buttonText}
          onPress={handlePrimaryClick}
        />
      </div>
    </div>
  )
}
